import { useEffectEvent } from '@funkit/connect-core'
import { useEffect, useRef } from 'react'

import type {
  WithdrawalFlowCallbacks,
  WithdrawalFlowConfig,
} from '../flow/withdrawalFlowConfig'
import { logger } from '../logging/logger'
import { useFunkitTranslation } from '../providers/FunkitI18nProvider'
import { executionErrorMessage } from '../utils/executionErrorMessage'
import {
  type DepositAddressOrHash,
  useCryptoWithdrawalExecution,
} from './useCryptoWithdrawalExecution'
import type { CryptoWithdrawalForm } from './useCryptoWithdrawalForm'

/**
 * Signing the withdrawal {@link CryptoWithdrawalForm} describes: the press, the
 * once-only hand-off to the outcome, and what the step shows while either is in
 * flight.
 *
 * Held apart from the step that renders it so the money path's invariants — one
 * hand-off, the source the signature was actually raised against, nothing
 * executed but the quote on screen — can be exercised without a rendered screen.
 */
/** What the recipient step is showing the withdrawal will land as. */
interface ReceiveLabels {
  usdLabel: string | undefined
  tokenLabel: string | undefined
}

export function useCryptoWithdrawalSubmit({
  config,
  callbacks,
  form,
  receiveLabels,
  onSubmitted,
}: {
  config: WithdrawalFlowConfig
  callbacks: WithdrawalFlowCallbacks
  /** The withdrawal being built, which is what gets signed. */
  form: CryptoWithdrawalForm
  receiveLabels: ReceiveLabels
  onSubmitted: (
    result: {
      depositAddressOrHash: DepositAddressOrHash
      amountLabel: string
    } & ReceiveLabels,
  ) => void
}) {
  const { t } = useFunkitTranslation()
  const { submission, submit } = useCryptoWithdrawalExecution({
    config,
    sourceToken: form.sourceToken,
    destinationToken: form.destinationToken ?? form.sourceToken,
    callbacks,
  })

  // Hand off exactly once. `onSubmitted` is an inline arrow from the flow, so it
  // is a new function every render — without the ref this re-fires on each one,
  // resetting the navigation stack in a loop.
  const handedOffRef = useRef(false)
  // The source the signature was raised against: a host config change can still
  // move the selection, and the completion screen must name what was sent.
  const submittedSourceLabel = useRef<string | undefined>(undefined)
  // The figures the withdrawal was signed against. The quote goes on refreshing
  // and the recipient step unmounts, so the outcome carries its own copy.
  const submittedFigures = useRef<ReceiveLabels>({
    usdLabel: undefined,
    tokenLabel: undefined,
  })
  const onSubmittedEvent = useEffectEvent(onSubmitted)
  useEffect(() => {
    if (submission.status !== 'submitted' || handedOffRef.current) {
      return
    }
    handedOffRef.current = true
    // The submission's own amount, not the field's: the input stays editable
    // while the signature is in flight, so the two can disagree by now.
    onSubmittedEvent({
      depositAddressOrHash: submission.depositAddressOrHash,
      amountLabel: `${submission.amount} ${submittedSourceLabel.current ?? form.sourceLabel}`,
      ...submittedFigures.current,
    })
  }, [submission, form.sourceLabel])

  // A landed signature still counts: the hand-off above is an effect, so the step
  // paints once more and would flash its editable chrome back before unmounting.
  const isInFlight =
    submission.status === 'submitting' || submission.status === 'submitted'

  return {
    isSubmitting: isInFlight,
    /** The button holds for the price and the screening too, not only the signature. */
    isBusy: form.isQuoteLoading || form.risk.isChecking || isInFlight,
    /** An unrecoverable failure from the last attempt, in the user's words. */
    executionErrorText:
      submission.status === 'failed'
        ? executionErrorMessage(t, submission.code, submission.message)
        : undefined,
    /** Executes the quote on screen. `form.canSubmit` is what gates the press. */
    submit: () => {
      if (!form.quote) {
        return
      }
      // Source/destination decision point, recorded before signing.
      logger.info('connect-rn:withdrawal:crypto_submit', {
        fromChainId: form.sourceToken.chainId,
        fromToken: form.sourceToken.address,
        toChainId: form.destinationToken?.chainId,
        toToken: form.destinationToken?.address,
      })
      submittedSourceLabel.current = form.sourceLabel
      submittedFigures.current = receiveLabels
      void submit({
        quote: form.quote.raw,
        recipientAddress: form.recipient,
        amount: form.quoteAmount,
      })
    },
  }
}
